/**
 * "Stored in" — the one control that says where a field's value goes.
 *
 * Shared by the body editor's inspector and the Fields tab, because they were
 * answering the same question differently and neither was answering it fully:
 * the inspector offered a raw `TXN.form.COLUMN` text box that rendered BLANK
 * for the commonest case of all (no map, so the engine falls back to the
 * primary's column of the same name), and the Fields tab offered a picker that
 * knew about transaction columns and not about document data. An author looking
 * for where their agreement date was stored found, correctly, nothing.
 *
 * Three things it does that a text box cannot:
 *
 * 1. **It never shows blank.** `destinationOf` resolves what the engine would
 *    actually do, so an undeclared field reads as the column it will land on,
 *    or as document data, rather than as an empty box.
 * 2. **It distinguishes DECLARED from INFERRED.** An inferred destination is
 *    shown as such, with one button to write it down — because the inference is
 *    exactly what moves under an author's feet when somebody adds a column to
 *    the transaction months later.
 * 3. **It knows document data has prerequisites.** Choosing it on a form that
 *    cannot deliver it says which of the three is missing and offers to set
 *    them, rather than accepting a declaration the save would ignore.
 */

import { createContext, useContext, useMemo } from 'react'
import type { SourceTxn } from './sources'
import type { FieldRow, FormModel } from './types'
import {
  describeDestination, destinationOf, documentDataReadiness, NOWHERE_WORDS,
} from './destination'
import { inputStyle, buttonStyle } from './styles'

export interface StoredInProps {
  field: FieldRow
  model: FormModel
  catalog: SourceTxn[]
  onChange: (fn: (f: FieldRow) => void) => void
  /**
   * Set the form up for document data — raise governance, switch the capture
   * mode, and leave naming the capture object to the Settings tab, which is
   * where the project's objects are listed.
   */
  onPrepareDocumentData?: (missing: string[]) => void
  /**
   * The caption above the control. The Fields tab draws its own, and the
   * inspector draws a SECTION heading before it — so an empty string means
   * draw none rather than defaulting one back in under the section's own
   * words.
   */
  heading?: string
}

export default function StoredIn({
  field, model, catalog, onChange, onPrepareDocumentData,
  heading = 'Where it is saved',
}: StoredInProps) {
  const dest = useMemo(
    () => destinationOf(field, model, catalog), [field, model, catalog])
  const readiness = useMemo(() => documentDataReadiness(model), [model])

  // Only a value-bearing field has a destination at all. A merge, a signature
  // and the repeat itself each store nothing, and offering a picker for them
  // would invite an author to set something the save will never read.
  const fixed = dest.kind === 'nowhere'
    && ['shown', 'signed', 'loop'].includes(dest.reason)

  const forms = (txnIdent: string) =>
    catalog.find((t) => t.ident === txnIdent.toUpperCase())?.forms || []

  const setTransaction = (ident: string, formNo: string, column: string) => {
    onChange((f) => {
      f.capture = 'typed'
      f.map = ident ? `${ident}.${formNo || '1'}.${(column || f.key).toUpperCase()}` : ''
    })
  }

  const setDocumentData = () => onChange((f) => {
    f.capture = 'sink'
    // A map and document data are contradictory claims about one value, and
    // the engine reads the map first — so the map goes, rather than sitting
    // there silently winning.
    f.map = ''
  })

  const [lead, noun, tail] = describeDestination(dest)

  if (fixed) {
    return (
      <>
        {heading && <Caption>{heading}</Caption>}
        <Note tone="muted">{NOWHERE_WORDS[dest.reason]}</Note>
      </>
    )
  }

  const chosen: 'transaction' | 'document-data' =
    dest.kind === 'document-data' ? 'document-data' : 'transaction'

  // Every source is read-only ⇒ there is no column anywhere for a typed value
  // to go, and "in a column of a transaction" is an option that leads nowhere.
  // That is exactly the case on a form built on a VISUAL: its columns are
  // worked out from other data, so anything the operator adds belongs to the
  // document. Offering the other option and letting the save quietly drop the
  // value is the failure this whole control exists to prevent.
  const noWritableSource = catalog.length > 0
    && catalog.every((t) => t.readOnly)

  return (
    <>
      {heading && <Caption>{heading}</Caption>}

      {/* What it resolves to today, in words, before any control. The author
          asked "where does this go" and this is the answer to that question;
          the pickers below are how it is changed. */}
      <div style={{
        fontSize: '0.8925rem', lineHeight: 1.6, marginBottom: 7,
        color: dest.kind === 'nowhere'
          ? 'var(--color-error)' : 'var(--color-text)',
      }}>
        {lead}{' '}
        <strong style={{ color: 'var(--color-accent)' }}>{noun}</strong>{' '}
        {tail}
      </div>

      {!dest.declared && dest.kind !== 'nowhere' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 8, flexWrap: 'wrap' }}>
          <Badge>Assumed</Badge>
          <button
            type="button"
            style={buttonStyle(false)}
            title="Write this destination down, so a change to the transaction cannot move it"
            onClick={() => {
              if (dest.kind === 'document-data') setDocumentData()
              else setTransaction(dest.txnIdent, dest.formNo, dest.column)
            }}
          >
            Lock it in
          </button>
        </div>
      )}

      {noWritableSource ? (
        <Note tone="muted">
          Everything this document reads is worked out rather than stored, so
          there is no column to write to. What is typed here is kept with the
          document.
        </Note>
      ) : (
        <select
          value={chosen}
          style={{ ...inputStyle, marginBottom: 6 }}
          onChange={(e) => {
            if (e.target.value === 'document-data') setDocumentData()
            else {
              const primary = catalog.find((t) => t.role === 'primary')
                || catalog[0]
              setTransaction(primary?.ident || '', '1', field.key)
            }
          }}
        >
          <option value="document-data">
            With this document only — no column needed
          </option>
          <option value="transaction">In a column of a transaction</option>
        </select>
      )}

      {chosen === 'transaction' && !noWritableSource && (
        <div style={{ display: 'grid', gap: 6 }}>
          <PickerLabel>Transaction</PickerLabel>
          <select
            value={dest.txnIdent}
            style={inputStyle}
            onChange={(e) => setTransaction(e.target.value, '1', field.key)}
          >
            {!catalog.length && <option value="">no transaction covered yet</option>}
            {catalog.map((t) => (
              <option key={t.ident} value={t.ident}>{t.title}</option>
            ))}
          </select>
          <PickerLabel>Part of it</PickerLabel>
          <select
            value={dest.formNo}
            style={inputStyle}
            onChange={(e) => setTransaction(dest.txnIdent, e.target.value,
              dest.column || field.key)}
          >
            {forms(dest.txnIdent).map((f) => (
              <option key={f.formNo} value={f.formNo}>
                {f.title} (form {f.formNo})
              </option>
            ))}
            {!forms(dest.txnIdent).length && (
              <option value={dest.formNo}>form {dest.formNo}</option>
            )}
          </select>
          <PickerLabel>Column</PickerLabel>
          <ColumnPicker
            columns={forms(dest.txnIdent)
              .find((f) => f.formNo === dest.formNo)?.columns || []}
            value={dest.column}
            onChange={(v) => setTransaction(dest.txnIdent, dest.formNo, v)}
          />
          {dest.table && (
            <Note tone="muted">
              The transaction owns the column; the document points at it.
            </Note>
          )}
        </div>
      )}

      {(chosen === 'document-data' || noWritableSource) && !readiness.ready && (
        <div style={{
          marginTop: 2, padding: '9px 10px', borderRadius: 7,
          border: '1px solid var(--color-warning)',
          background: 'var(--color-warning-weak)',
          fontSize: '0.8625rem', lineHeight: 1.6, color: 'var(--color-text)',
        }}>
          <strong>This form cannot keep its own data yet.</strong>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {readiness.missing.includes('governance') && (
              <li>Keep a record of each document</li>
            )}
            {readiness.missing.includes('mode') && (
              <li>Let it hold values of its own</li>
            )}
            {readiness.missing.includes('object') && (
              <li>Choose where those values are held, on the Settings tab</li>
            )}
          </ul>
          {onPrepareDocumentData && readiness.missing.some(
            (m) => m !== 'object') && (
            <button
              type="button"
              style={{ ...buttonStyle(true), marginTop: 8 }}
              onClick={() => onPrepareDocumentData(readiness.missing)}
            >
              Set it up
            </button>
          )}
        </div>
      )}

      {dest.kind === 'nowhere' && !fixed && (
        <Note tone="error">{NOWHERE_WORDS[dest.reason]}</Note>
      )}
    </>
  )
}

/** A column of one form, by its own heading — or free text when unknown. */
function ColumnPicker({ columns, value, onChange }: {
  columns: { dbName: string; heading: string; key: boolean }[]
  value: string
  onChange: (v: string) => void
}) {
  if (!columns.length) {
    return (
      <input
        value={value}
        placeholder="a column"
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace' }}
      />
    )
  }
  const known = columns.some((c) => c.dbName === value.toUpperCase())
  return (
    <select value={value.toUpperCase()} style={inputStyle}
      onChange={(e) => onChange(e.target.value)}>
      <option value="">— choose a column —</option>
      {!known && value && <option value={value.toUpperCase()}>{value}</option>}
      {columns.map((c) => (
        <option key={c.dbName} value={c.dbName}>
          {c.heading}{c.key ? ' (key)' : ''}
        </option>
      ))}
    </select>
  )
}

/** A name above one of the three destination pickers. */
function PickerLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: '0.8225rem', fontWeight: 600, marginBottom: -2,
      color: 'var(--color-text-secondary)' }}>{children}</div>
  )
}

function Caption({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.7,
      textTransform: 'uppercase', color: 'var(--color-text-secondary)',
      marginTop: 12, marginBottom: 5,
    }}>{children}</div>
  )
}

function Badge({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: '0.8125rem', fontWeight: 700, letterSpacing: 0.4,
      textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20,
      border: '1px solid var(--color-warning)',
      color: 'var(--color-warning)',
    }}>{children}</span>
  )
}

function Note({ children, tone }: {
  children: React.ReactNode; tone: 'muted' | 'error'
}) {
  return (
    <div style={{
      fontSize: '0.8425rem', lineHeight: 1.55, marginTop: 5,
      color: tone === 'error' ? 'var(--color-error)'
        : 'var(--color-text-secondary)',
    }}>{children}</div>
  )
}


/**
 * The form and the catalog, for the Fields tab's editors.
 *
 * A context rather than two more props threaded through `GroupCard` →
 * `FieldRowView` → `EntryEditor`: the tab already holds both at its root, and
 * three layers of pass-through for something every editor needs is how a prop
 * list stops being readable. The inspector passes them directly, because it has
 * one consumer and no chain.
 */
export interface StoredInScope {
  model: FormModel | null
  catalog: SourceTxn[]
  /**
   * WHICH template is being edited.
   *
   * A composed form has several, and "where does this field go" is answered
   * against the fields of the one on screen — a section's `OUTPUT_KG` is not
   * the master's. Zero for every form with a single template.
   */
  templateIndex?: number
  onPrepareDocumentData?: (missing: string[]) => void
}

export const StoredInContext = createContext<StoredInScope>({
  model: null, catalog: [], templateIndex: 0,
})

/** `StoredIn`, wired to the surrounding scope. Renders nothing without one. */
export function BoundStoredIn({ field, onChange, heading }: {
  field: FieldRow
  onChange: (fn: (f: FieldRow) => void) => void
  heading?: string
}) {
  const scope = useContext(StoredInContext)
  if (!scope.model) return null
  return (
    <StoredIn
      field={field}
      model={scope.model}
      catalog={scope.catalog}
      onChange={onChange}
      onPrepareDocumentData={scope.onPrepareDocumentData}
      heading={heading}
    />
  )
}
